import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On non-home pages, always show solid header (no transparent state)
  const showSolid = scrolled || !isHome;

  // After navigating to home with a hash, scroll to that section
  useEffect(() => {
    if (isHome && location.hash) {
      const id = location.hash;
      // Wait for next frame so the home page is mounted
      setTimeout(() => {
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [isHome, location.hash, location.pathname]);

  const navLinks = [
    { label: '施設紹介', href: '#about' },
    { label: '設備・機器', href: '#system' },
    { label: '料金プラン', href: '#price' },
    { label: '無料体験', href: '#experience' },
    { label: 'お客様の声', href: '#voice' },
    { label: 'アクセス', href: '#access' },
    { label: 'よくある質問', href: '#faq' },
  ];

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to home with hash
      navigate('/' + href);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          showSolid
            ? 'bg-white/95 backdrop-blur-md border-b border-[#1a1a3e]/8 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="px-6 lg:px-12 h-16 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center"
            onClick={handleLogoClick}
          >
            <img
              src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/63a3b48bb43cc521cdf9dc975b536e3e.png"
              alt="Golph.club Suminoe"
              className={`h-7 w-auto object-contain transition-all duration-300 ${showSolid ? '' : 'brightness-0 invert'}`}
            />
          </a>

          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap font-medium ${
                  showSolid
                    ? 'text-[#1a1a3e]/60 hover:text-[#1a1a3e]'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/golph.club/"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden md:flex w-8 h-8 items-center justify-center transition-colors duration-200 ${
                showSolid ? 'text-[#1a1a3e]/50 hover:text-[#e85d2c]' : 'text-white/70 hover:text-white'
              }`}
            >
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a
              href="https://tdbs.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 bg-[#47d6d4] text-white text-sm font-bold px-5 py-2 rounded-full hover:bg-[#3bc4c2] transition-all duration-200 whitespace-nowrap"
            >
              予約する
            </a>
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${
                showSolid ? 'text-[#1a1a3e]' : 'text-white'
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="メニュー"
            >
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            menuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
          } bg-white/98 backdrop-blur-md border-t border-[#1a1a3e]/8`}
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-left text-[#1a1a3e]/70 hover:text-[#47d6d4] text-sm py-3 border-b border-[#1a1a3e]/5 transition-colors cursor-pointer font-medium"
              >
                {link.label}
              </button>
            ))}
            <a
              href="https://www.instagram.com/golph.club/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#1a1a3e]/50 hover:text-[#e85d2c] text-sm py-3 transition-colors mt-1"
            >
              <i className="ri-instagram-line text-lg"></i>
              Instagram
            </a>
          </div>
        </div>
      </header>

      {/* Mobile Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <a
          href="https://tdbs.me/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center bg-[#47d6d4] text-white font-bold text-base py-4 whitespace-nowrap"
        >
          予約する
        </a>
      </div>
    </>
  );
}
